import React, { useRef, useEffect } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import WhiteboardController from './controllers/WhiteboardController';
import { ALLOWED_IMAGE_TYPES } from './shapes/ShapeImage';
import { ALLOWED_VIDEO_TYPES } from './shapes/ShapeVideo';
import ToolPaste from './tools/ToolPaste';

interface FileCaptureProps {
  canvasContainer: React.MutableRefObject<HTMLDivElement | null>;
}

async function handleSVGFile(controller: WhiteboardController, file: File) {
  const text = await file.text();
  await ToolPaste.pasteSVGTextAsShapes(controller, text);
}

const FileCapture: React.FC<FileCaptureProps> = ({ canvasContainer }) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const controller = useController(WhiteboardController);

  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    if (file) {
      void controller.actionUploadImage(file);
    }
    // Reset value so onChange is triggered again if the same file is selected.
    event.target.value = '';
  };

  useEffect(() => {
    if (!imageInputRef.current) return;
    controller.actionSetImageInput(imageInputRef.current);
  }, [controller, imageInputRef]);

  // Handle drag and drop operations.
  useEffect(() => {
    if (!canvasContainer.current) return;

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!e.currentTarget || !e.dataTransfer) {
        return;
      }

      const el = e.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      const point = controller.canvas.pointToCanvasCoords({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });

      for (const item of e.dataTransfer.items) {
        const file = item?.getAsFile();
        if (file) {
          if (
            file.type === 'image/svg+xml' ||
            file.name.toLowerCase().endsWith('.svg')
          ) {
            void handleSVGFile(controller, file);
          } else {
            void controller.actionUploadImage(file, { x: point.x, y: point.y });
          }
        }
        point.x += 50;
        point.y += 50;
      }
    };

    const div = canvasContainer.current;
    div.addEventListener('drop', handleDrop);

    return () => {
      div.removeEventListener('drop', handleDrop);
    };
  }, [canvasContainer, controller]);

  return (
    <input
      style={{ display: 'none' }}
      ref={imageInputRef}
      onChangeCapture={onFileSelected}
      accept={[...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES].join(',')}
      type='file'
      id='whiteboard-image-input'
    />
  );
};

export default ApplicationView(FileCapture);
